import * as React from 'react';
import { Pencil } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { GATE_FIELD_LABEL_CLASS } from './GateFormControls';

export interface GateIdentity {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    /** The suburb as the reader typed it, e.g. "Auckland, New Zealand". */
    suburb?: string;
    /** The member's profile photo; initials stand in when they have none. */
    avatarUrl?: string;
}

export interface GateIdentityFieldsProps {
    /**
     * The account the fields are prefilled from. `null` for a guest, who has
     * nothing to summarise and so gets the fields plainly.
     */
    identity: GateIdentity | null;
    expanded: boolean;
    onExpandedChange: (next: boolean) => void;
    /** Mirrors the form's disabled state so nothing toggles mid-send. */
    disabled?: boolean;
    /**
     * Sign this member out so someone else can take over the enquiry. Omitted
     * where a surface has nowhere to land afterwards.
     */
    onSwitchAccount?: () => void;
    /**
     * Whether the reader has changed anything since the panel opened. Drives
     * the closing action: an untouched panel offers Close, an edited one
     * offers Save details — the label and the weight both say whether
     * pressing it writes anything.
     */
    dirty?: boolean;
    className?: string;
    children: React.ReactNode;
}

const initialsFor = ({ firstName, lastName, email }: GateIdentity) => {
    const fromName = [firstName, lastName]
        .map((part) => part.trim().charAt(0))
        .filter(Boolean)
        .join('');
    return (fromName || email.trim().charAt(0)).toUpperCase();
};

/**
 * The gate's contact fields, collapsed behind a one-line summary of the
 * signed-in reader's account.
 *
 * A member arrives with name, email, phone and suburb already filled from their
 * profile and the overwhelming majority send without touching any of them. As
 * five inputs that reads as a form to complete; as a line to check it reads as
 * what it is — a confirmation — and the message field moves up to where the
 * reader's attention already is.
 *
 * The fields stay mounted while collapsed rather than unmounting: they carry
 * validation state, a suburb autocomplete with its own popover, and session
 * persistence on blur, none of which should churn each time the card opens.
 * Keeping them mounted also keeps the disclosure's `aria-controls` target in
 * the document, and leaves required-field validation working on a collapsed
 * card — the caller opens it when something is missing.
 */
export const GateIdentityFields = ({
    identity,
    expanded,
    onExpandedChange,
    disabled = false,
    onSwitchAccount,
    dirty = false,
    className,
    children,
}: GateIdentityFieldsProps) => {
    const regionId = React.useId();
    const editRef = React.useRef<HTMLButtonElement>(null);
    const regionRef = React.useRef<HTMLDivElement>(null);
    // Expanding and collapsing swap which control is on screen, so move focus
    // deliberately. Left alone, activating a control that then unmounts drops
    // focus to the body, and inside the gate's focus trap that throws a
    // keyboard reader back to the top of the modal.
    const pendingFocus = React.useRef<'fields' | 'edit' | null>(null);

    React.useEffect(() => {
        const target = pendingFocus.current;
        pendingFocus.current = null;
        if (target === 'edit') editRef.current?.focus();
        if (target === 'fields') {
            regionRef.current
                ?.querySelector<HTMLInputElement>('input:not([disabled])')
                ?.focus();
        }
    }, [expanded]);

    if (!identity) return <>{children}</>;

    const setExpanded = (next: boolean) => {
        pendingFocus.current = next ? 'fields' : 'edit';
        onExpandedChange(next);
    };

    const fullName = `${identity.firstName} ${identity.lastName}`.trim();
    // Only the values the account actually holds — a member with no phone
    // saved must not be shown a stray separator where one would go.
    const detail = [identity.email, identity.phone, identity.suburb]
        .filter(Boolean)
        .join(' · ');

    // Both states stay mounted and swap with the `hidden` attribute, which
    // Tailwind's preflight backs with `display: none !important`. The fields
    // carry validation state, a suburb autocomplete with its own popover and
    // session persistence on blur; unmounting them on every open would churn
    // all three and leave the caller unable to focus a field it just revealed.
    return (
        <div className={cn('contents', className)}>
            {/* No display utility on this element — `hidden` is what closes
                the summary, and a Tailwind display class would fight it. */}
            <div hidden={expanded}>
                <div className="flex flex-col gap-1.5">
                    <span className={GATE_FIELD_LABEL_CLASS}>
                        {`Enquiring as`}
                    </span>
                    <div className="flex items-center gap-3 rounded-[0.625rem] bg-[#f6f6f6] p-3">
                        {identity.avatarUrl ? (
                            <img
                                src={identity.avatarUrl}
                                alt=""
                                className="size-10 shrink-0 rounded-full object-cover"
                            />
                        ) : (
                            <span
                                aria-hidden
                                className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#181818] text-[0.8125rem] font-medium text-white"
                            >
                                {initialsFor(identity)}
                            </span>
                        )}
                        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <span className="truncate text-[1rem] font-medium tracking-[-0.02em] text-[#161616]">
                                {fullName || identity.email}
                            </span>
                            {!!detail && (
                                <span className="truncate text-[0.8125rem] text-[#828282]">
                                    {detail}
                                </span>
                            )}
                        </div>
                        <button
                            ref={editRef}
                            type="button"
                            disabled={disabled}
                            aria-expanded={false}
                            aria-controls={regionId}
                            aria-label="Edit your details"
                            onClick={() => setExpanded(true)}
                            className="shrink-0 rounded-full p-1.5 text-black/40 hover:text-black/70 disabled:opacity-50"
                        >
                            <Pencil aria-hidden className="size-4" />
                        </button>
                    </div>
                </div>
            </div>
            {/* Expanded, the fields ARE the content — the same plain layout a
                guest gets, only prefilled. Showing the summary above them
                would print every value twice and frame editing as a sub-task
                of a panel rather than the form the reader is filling in. */}
            {/* Ruled off below: expanded, these fields run to roughly the
                height of the rest of the form, and without a close the reader
                cannot see where their own details end and the enquiry begins. */}
            <div
                ref={regionRef}
                hidden={!expanded}
                id={regionId}
                className="flex flex-col gap-5.5 border-b border-black/8 pb-5.5"
            >
                {children}
                <div className="flex items-center justify-between gap-3">
                    {/* The reader who finds someone else's details here needs
                        a way out, and it belongs beside the details rather
                        than in the header — this is the moment they notice. */}
                    {onSwitchAccount ? (
                        <span className="text-[0.8125rem] text-[#828282]">
                            {`Not you? `}
                            <button
                                type="button"
                                disabled={disabled}
                                onClick={onSwitchAccount}
                                className="font-medium text-[#161616] underline disabled:opacity-50"
                            >
                                {`Switch account`}
                            </button>
                        </span>
                    ) : (
                        <span />
                    )}
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={() => setExpanded(false)}
                        className={cn(
                            'h-10.5 shrink-0 rounded-full px-6 text-[0.875rem] font-medium disabled:opacity-50',
                            dirty
                                ? 'bg-[#181818] text-[#f3f3f3]'
                                : 'bg-[#f0f0f0] text-[#656565]',
                        )}
                    >
                        {dirty ? `Save details` : `Close`}
                    </button>
                </div>
            </div>
        </div>
    );
};
